import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  Injector,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  DEFAULT_SELECTION_CONFIG,
  isSelectable,
  type DocumentItem,
  type SelectionConfig,
} from '@document-explorer/core';
import { DEFAULT_INTERACTION, type ActionDef, type InteractionConfig, type ItemTemplate } from './types.js';
import { DeIconComponent } from './icon.component.js';
import { DeUiIconComponent } from './ui-icon.component.js';
import { DeActionsMenuComponent } from './actions-menu.component.js';
import { DeCardMetaPipe } from './cell-value.pipe.js';
import { RovingFocus } from './roving-focus.js';

@Component({
  selector: 'de-grid-view',
  standalone: true,
  imports: [NgTemplateOutlet, DeIconComponent, DeUiIconComponent, DeActionsMenuComponent, DeCardMetaPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!--
      A flat set of selectable cards is a listbox, not a grid: the option role
      carries aria-selected, and gridcell would require an intervening row.
    -->
    <div
      #container
      class="de-grid"
      role="listbox"
      (focusin)="roving.noteFocusIn()"
      (focusout)="roving.noteFocusOut($event)"
      aria-label="Documents"
      [attr.aria-multiselectable]="selectionEnabled() ? true : null"
    >
      @for (item of items(); track item.id; let index = $index) {
        <div
          role="option"
          class="de-card"
          [attr.aria-selected]="selectedIds().has(item.id)"
          [attr.data-de-index]="index"
          [attr.tabindex]="roving.tabIndexFor(index)"
          (keydown)="onKeyDown($event)"
          (focus)="roving.setActive(index)"
          (click)="onClick(item, $event)"
          (dblclick)="onDoubleClick(item)"
        >
          @if (selectionEnabled() && canSelect(item)) {
            <!-- Not a real checkbox: an option may not contain interactive
                 children. The card itself toggles on click or Space. -->
            <span
              class="de-card-check"
              [class.de-card-check--on]="selectedIds().has(item.id)"
              aria-hidden="true"
            >
              @if (selectedIds().has(item.id)) {
                <de-ui-icon name="check" />
              }
            </span>
          }

          @if (templateFor(item); as template) {
            <ng-container *ngTemplateOutlet="template; context: { $implicit: item, item: item }" />
          } @else {
            <de-icon [item]="item" />
            <span class="de-card-name" [attr.title]="item.name">{{ item.name }}</span>
            <span class="de-card-meta">{{ item | deCardMeta: locale() }}</span>
          }

          @if (actions().length > 0) {
            <de-actions-menu [item]="item" [actions]="actions()" (action)="action.emit($event)" />
          }
        </div>
      }
    </div>
  `,
})
export class DeGridViewComponent {
  readonly items = input<DocumentItem[]>([]);
  readonly actions = input<ActionDef[]>([]);
  readonly interaction = input<Required<InteractionConfig>>(DEFAULT_INTERACTION);
  readonly selectionConfig = input<SelectionConfig>(DEFAULT_SELECTION_CONFIG);
  readonly selectedIds = input<ReadonlySet<string>>(new Set<string>());
  readonly locale = input<string | undefined>(undefined);
  readonly fileTemplate = input<ItemTemplate | undefined>(undefined);
  readonly folderTemplate = input<ItemTemplate | undefined>(undefined);

  readonly open = output<DocumentItem>();
  readonly toggleSelect = output<DocumentItem>();
  readonly selectRange = output<DocumentItem>();
  readonly selectAll = output<void>();
  readonly clearSelection = output<void>();
  readonly navigateUp = output<void>();
  readonly action = output<{ actionId: string; item: DocumentItem }>();

  private readonly container = viewChild<ElementRef<HTMLElement>>('container');
  private readonly columnCount = signal(1);
  protected readonly roving = new RovingFocus(() => this.container(), inject(Injector));

  protected readonly selectionEnabled = computed(() => this.selectionConfig().enabled);

  /** See the note on the list view: contents, not array identity. */
  private readonly listSignature = computed(() => {
    const items = this.items();
    return `${items.length}:${items[0]?.id ?? ''}:${items[items.length - 1]?.id ?? ''}`;
  });

  private observer: ResizeObserver | undefined;

  constructor() {
    effect(() => {
      this.listSignature();
      this.roving.syncToList();
      // Arrow keys should follow the visual grid, and the track count comes
      // from `auto-fill`, so it has to be measured rather than assumed.
      this.measureColumns();
    });

    effect((onCleanup) => {
      const host = this.container()?.nativeElement;
      if (!host || typeof ResizeObserver === 'undefined') return;
      this.observer = new ResizeObserver(() => this.measureColumns());
      this.observer.observe(host);
      onCleanup(() => this.observer?.disconnect());
    });
  }

  private measureColumns(): void {
    const host = this.container()?.nativeElement;
    if (!host || typeof getComputedStyle === 'undefined') return;
    const tracks = getComputedStyle(host).gridTemplateColumns;
    this.columnCount.set(Math.max(1, tracks ? tracks.split(' ').filter(Boolean).length : 1));
  }

  protected canSelect(item: DocumentItem): boolean {
    return isSelectable(item, this.selectionConfig());
  }

  protected templateFor(item: DocumentItem): ItemTemplate | undefined {
    return item.type === 'folder' ? this.folderTemplate() : this.fileTemplate();
  }

  protected onClick(item: DocumentItem, event: MouseEvent): void {
    const behaviour = item.type === 'folder' ? this.interaction().folderClick : this.interaction().fileClick;
    const selectable = this.selectionEnabled() && this.canSelect(item);
    if (event.shiftKey && selectable) return this.selectRange.emit(item);
    if ((event.metaKey || event.ctrlKey) && selectable) return this.toggleSelect.emit(item);
    if (behaviour === 'open') return this.open.emit(item);
    if (behaviour === 'select' && this.selectionEnabled()) this.toggleSelect.emit(item);
  }

  protected onDoubleClick(item: DocumentItem): void {
    if (this.interaction().doubleClick === 'open') this.open.emit(item);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    this.roving.handleKey(
      event,
      this.items(),
      {
        onOpen: (item) => this.open.emit(item),
        onToggleSelect: (item) => this.toggleSelect.emit(item),
        onSelectRange: (item) => this.selectRange.emit(item),
        onSelectAll: () => this.selectAll.emit(),
        onNavigateUp: () => this.navigateUp.emit(),
        onClearSelection: () => this.clearSelection.emit(),
      },
      this.columnCount(),
    );
  }
}
